import { ReactElement, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import { Box, Typography } from '@mui/material';

import B3Spin from '@/components/spin/B3Spin';
import { B3PaginationTable } from '@/components/table/B3PaginationTable';
import { TableColumnItem } from '@/components/table/B3Table';
// import { useMobile, useSort } from '@/hooks';
// import { GlobaledContext } from '@/shared/global';
import { getInvoiceList, getInvoicePdf } from '@/shared/service/genesee/invoice'
import {
  currencyFormatInfo,
  displayFormat,
  handleGetCorrespondingCurrencyToken,
  snackbar,
} from '@/utils';
import b2bLogger from '@/utils/b3Logger';

//import B3Pulldown from './components/B3Pulldown';
import PrintTemplate from './components/PrintTemplate';
import { defaultSortKey } from '@/pages/MultiInvoice/utils/config';
import { formattingNumericValues } from '../Invoice/utils/payment';
import { MultiInvoiceItemCard } from './MultiInvoiceItemCard';
import {
  MultiInvoiceQuery,
  sortIdArr,
  InvoiceList,
  InvoiceListNode
} from '@/pages/MultiInvoice/utils/types';
import { useSort } from '@/hooks'

export interface FilterSearchProps {
  [key: string]: string | number | null;
  q: string;
}

interface PaginationTableRefProps extends HTMLInputElement {
  getList: () => void;
  getCacheList: () => void;
  setCacheAllList: (items?: InvoiceList[]) => void;
  setList: (items?: InvoiceListNode[]) => void;
  getSelectedValue: () => void;
}

const initFilter = {
  q: '',
  first: 10,
  offset: 0,
  orderBy: `-${sortIdArr[defaultSortKey]}`,
};

function MultiInvoice() {
  const b3Lang = useB3Lang();

  const navigate = useNavigate();
  // const [isMobile] = useMobile();
  const paginationTableRef = useRef<PaginationTableRefProps | null>(null);

  const { decimal_places: decimalPlaces = 2 } = currencyFormatInfo();

  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [list, setList] = useState<InvoiceListNode[]>([]);

  const [filterData, setFilterData] = useState<Partial<FilterSearchProps> | null>();

  const [filterChangeFlag, setFilterChangeFlag] = useState(false);
  const [filterLists, setFilterLists] = useState<InvoiceListNode[]>([]);

  // const {
  //   state: { bcLanguage },
  // } = useContext(GlobaledContext);

  const [handleSetOrderBy, order, orderBy] = useSort(
    sortIdArr,
    defaultSortKey,
    filterData,
    setFilterData,
  );

  const location = useLocation();

  const isFiltering = (filterData: Partial<FilterSearchProps>) =>
    Object.keys(filterData).some(
      (key) => key !== 'first' && key !== 'offset' && key !== 'orderBy' && filterData[key],
    );

  const cacheFilterLists = (edges: InvoiceListNode[]) => {
    if (filterChangeFlag) {
      setFilterLists(edges);
      setFilterChangeFlag(false);
      return;
    }

    if (!filterLists.length) setFilterLists(edges);

    const copyCacheFilterList = [...filterLists];

    setFilterLists(copyCacheFilterList);
  };

  const handleViewInvoice = async (item: InvoiceList) => {
    try {
      const pdfUrl = item.PDF;

      if (!pdfUrl) {
        snackbar.error(b3Lang('invoice.pdfUrlResolutionError'));
        return;
      }

      const { href } = window.location;
      if (!href.includes('invoice')) {
        return;
      }

      const blob = await getInvoicePdf(pdfUrl)
      const pdfBlobUrl = URL.createObjectURL(blob);

      window.open(pdfBlobUrl, '_blank', 'fullscreen=yes');
    } catch (err) {
      b2bLogger.error(err);
    }
  };

  useEffect(() => {
      setFilterData({
        ...initFilter,
      });
  }, [location]);

  function sortInvoicesWithFormat(
    array: InvoiceList[],
    query: MultiInvoiceQuery
  ): InvoiceList[] {
    const { orderBy } = query;

    if (!orderBy) {
      throw new Error("Invalid query: 'orderBy' property is required");
    }

    // Determine the property and sort order
    const order = orderBy.startsWith('-') ? 'desc' : 'asc';
    const property = orderBy.startsWith('-') ? orderBy.slice(1) : orderBy;

    // Sort the array
    return array.sort((a, b) => {
      if (a[property as keyof InvoiceList] < b[property as keyof InvoiceList]) {
        return order === 'asc' ? -1 : 1;
      }
      if (a[property as keyof InvoiceList] > b[property as keyof InvoiceList]) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  const fetchList = async (params: MultiInvoiceQuery) => {

    let { Invoices } = await getInvoiceList();

    Invoices = sortInvoicesWithFormat(Invoices, params);

    const invoicesList: InvoiceListNode[] = Invoices;

    setList(invoicesList);

    if (filterData && isFiltering(filterData) && invoicesList.length) {
      cacheFilterLists(invoicesList);
    } else {
      setFilterLists([]);
    }

    return {
      edges: Invoices
    };
  };

  const columnAllItems: TableColumnItem<InvoiceList>[] = [
    {
      key: 'id',
      title: b3Lang('multiinvoice.headers.invoice'),
      isSortable: true,
      render: (item: InvoiceList) => (
        <Box
          sx={{
            color: '#000000',
            cursor: 'pointer',
            ':hover': {
              textDecoration: 'underline',
            },
          }}
          onClick={() => {
            if (item.PDF) {
              handleViewInvoice(item);
            }
          }}
        >
          {item?.InvoiceNo ? item?.InvoiceNo : item?.InvoiceNo}
        </Box>
      ),
      width: '8%',
    },
    {
      key: 'orderNumber',
      title: b3Lang('invoice.headers.order'),
      isSortable: true,
      render: (item: InvoiceList) => {
        const hasWebOrderId = !!item.WebOrderId;
        const cleanWebOrderId = item.WebOrderId?.replace(/[A-Za-z]/g, '');

        return hasWebOrderId ? (
          <Box
            sx={{
              color: '#000000',
              cursor: 'pointer',
              ':hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={() => {
              navigate(`/orderDetail/${cleanWebOrderId || '-'}`);
            }}
          >
            {item.WebOrderId}
          </Box>
        ) : (
          <Box sx={{ color: '#000000' }}>
            {'-'}
          </Box>
        );
      },
      width: '8%',
    },
    {
      key: 'createdAt',
      title: b3Lang('multiinvoice.headers.invoiceDate'),
      isSortable: true,
      render: (item: InvoiceList) => `${item.InvoiceDate ? displayFormat(+item.InvoiceDate) : '–'}`,
      width: '10%',
    },
    {
      key: 'updatedAt',
      title: b3Lang('multiinvoice.headers.dueDate'),
      isSortable: true,
      render: (item: InvoiceList) => {

        return (
          <Typography
            sx={{
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '14px',
            }}
          >
            {`${item.InvoiceDueDate ? displayFormat(+item.InvoiceDueDate) : '–'}`}
          </Typography>
        );
      },
      width: '10%',
    },
    {
      key: 'originalBalance',
      title: b3Lang('multiinvoice.headers.invoiceTotal'),
      isSortable: true,
      render: (item: InvoiceList) => {
        const { InvoiceTotal } = item;
        const originalAmount = formattingNumericValues(+InvoiceTotal, decimalPlaces);

        const token = handleGetCorrespondingCurrencyToken('USD');

        return `${token}${originalAmount || 0}`;
      },
      width: '10%',
    },
    // {
    //   key: 'companyName',
    //   title: b3Lang('invoice.headers.action'),
    //   render: (row: InvoiceList) => {
    //     const { InvoiceNo } = row;
    //     let actionRow = row;
    //     if (selectedPay.length > 0) {
    //       const currentSelected = selectedPay.find((item: InvoiceListNode) => {
    //         const {
    //           node: { InvoiceNo: selectedId },
    //         } = item;
    //
    //         return +selectedId === +InvoiceNo;
    //       });
    //
    //       if (currentSelected) {
    //         actionRow = currentSelected.node;
    //       }
    //     }
    //
    //     return (
    //       <B3Pulldown
    //         row={actionRow}
    //         setInvoiceId={setCurrentInvoiceId}
    //         handleOpenHistoryModal={setIsOpenHistorys}
    //         setIsRequestLoading={setIsRequestLoading}
    //       />
    //     );
    //   },
    //   width: '8%',
    // },
  ];

  return (
    <B3Spin isSpinning={isRequestLoading}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          position: 'relative',
        }}
      >
        <B3PaginationTable
          ref={paginationTableRef}
          columnItems={columnAllItems}
          rowsPerPageOptions={[10, 20, 30]}
          getRequestList={fetchList}
          searchParams={filterData}
          showPagination={false}
          isCustomRender={false}
          requestLoading={setIsRequestLoading}
          tableKey="id"
          showCheckbox={false}
          showSelectAllCheckbox={false}
          disableCheckbox={true}
          applyAllDisableCheckbox={false}
          CollapseComponent={PrintTemplate}
          sortDirection={order}
          orderBy={orderBy}
          sortByFn={handleSetOrderBy}
          isSelectOtherPageCheckbox
          hover
          renderItem={(
            row: InvoiceList,
            index?: number,
            checkBox?: (disable?: boolean) => ReactElement,
          ) => (
            <MultiInvoiceItemCard
              item={row}
              checkBox={checkBox}
              handleViewInvoice={handleViewInvoice}
              setIsRequestLoading={setIsRequestLoading}
              handleGetCorrespondingCurrency={handleGetCorrespondingCurrencyToken}
              addBottom={list.length - 1 === index}
            />
          )}
        />
      </Box>
    </B3Spin>
  );
}

export default MultiInvoice;
